import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor } from '@angular/common/http';
import { Observable, from, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { AuthenticationService } from '../services/auth.service';
import { RETRIED_AFTER_REFRESH } from './auth-http.context';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {

    constructor(private authenticationService: AuthenticationService) { }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return next.handle(request).pipe(catchError(err => {
            if (err.status === 401) {
                if (this.authenticationService.isIdentityRefreshRequest(request.url)) {
                    this.authenticationService.clearSessionAndRedirectToLogin();
                    return throwError(() => err);
                }
                if (request.context.get(RETRIED_AFTER_REFRESH)) {
                    return throwError(() => err);
                }
                return this.refreshTokenAndRetry(request, next);
            }
            let error = err?.error?.message || err?.error?.messages || err;
            if (err?.error?.maxLimitForProduct) {
                error = {
                    maxLimitForProduct: err?.error?.maxLimitForProduct,
                    messages: error
                }
            }
            return throwError(() => error);
        }));
    }

    private refreshTokenAndRetry(
        request: HttpRequest<any>,
        next: HttpHandler
    ): Observable<HttpEvent<any>> {
        const refreshToken = this.authenticationService.refreshToken;

        if (!refreshToken) {
            this.authenticationService.clearSessionAndRedirectToLogin();
            return throwError(() => new Error('No refresh token'));
        }

        return from(this.authenticationService.refreshSession('401-retry')).pipe(
            catchError((err) => {
                this.authenticationService.clearSessionAndRedirectToLogin();
                return throwError(() => err);
            }),
            switchMap((accessToken) => {
                if (!accessToken) {
                    this.authenticationService.clearSessionAndRedirectToLogin();
                    return throwError(() => new Error('Session refresh failed'));
                }
                const retriedRequest = this.addTokenHeader(
                    request.clone({
                        context: request.context.set(RETRIED_AFTER_REFRESH, true),
                    }),
                    accessToken
                );
                return next.handle(retriedRequest);
            })
        );
    }

    private addTokenHeader(request: HttpRequest<any>, token: string) {
        const headers: Record<string, string> = {
            Authorization: `Bearer ${token}`,
        };
        const orgId = this.authenticationService.resolveActiveOrganizationId();
        if (orgId && this.authenticationService.isIdentityAccessToken(token)) {
            headers['X-Organization-Id'] = orgId;
        }
        return request.clone({ setHeaders: headers });
    }
}
